/*
  lp-kit.js — behaviour for Daphne's lp-* landing page sections (v1.4).

  Loaded as a module by every lp-* section, so it runs once per page however
  many sections include it. Each section instance is set up and torn down on
  its own, keyed by its element, so the theme editor can add, remove, reload
  and reorder sections without leaking listeners.

  Add to cart uses the site's own cart (window.cart from the theme: add, then
  open its cart drawer), the same path as the product page. The shopper stays
  on the page. It does not fire any analytics. The store's existing app pixels (Elevar,
  Triple Whale and others) observe the cart request itself. It dispatches
  `lp:cart:added` / `lp:cart:error` DOM events for anyone who needs them.

  Changing this file changes every landing page. See lp-kit/components.md.

  v1.2 (additive): offer gallery (thumbnails / swipe), the showcase price row
  follows the chosen tier, showcase draft tiers can be selected as a preview,
  and filler tiers (mockups) show their draft price but can never be added. Elements marked data-lp-draft-safe (gallery buttons, draft preview
  tiers) stay usable on a draft; every purchase control is still disabled.

  v1.3 (additive): an offer's gift with purchase rides along as its own line in
  the same add (only while it's unlocked), first-box prices and the subscribe
  terms line follow the chosen tier and plan, a tier's discount code is applied
  to the site cart when subscribed (and cleared again when it no longer
  applies), and the cart line key adds the theme buy box's hidden properties.
  Offers that set none of these behave exactly as in v1.2.

  v1.4.4 (additive): flavor options. The buy box's "Flavor option" tiles swap the
  selected flavor's gallery slides, star cue, heading, line, quote and bullets in place
  and point every pack tile at that flavor's variant, so Add to cart posts what the
  shopper sees. No page load; the chosen pack is kept; ?flavor=<name> preselects one.
  v1.4 (additive): Promo offers. Each tile's saving and first-box caption, the
  chosen tier's "What's inside" list (and the gift in it), the price in the button
  and the subscribe perks' prices follow the chosen tier and purchase type, all from
  the same price table as the tiles. The button keeps its price while it isn't busy.
  Offers that set none of these behave exactly as in v1.3.
*/

const instances = new WeakMap()
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const CAMPAIGN_PARAM = /^utm_[a-z]+$/i

/* ---------- CTA buttons: one delegated listener for the whole page ---------- */

function findAnchor(name) {
  if (!name) return null
  return document.querySelector(`[data-lp-anchor="${CSS.escape(name)}"]`)
}

// Draft offers (no product connected yet): nothing on the page may lead to a
// purchase. Scroll buttons that only move to the draft offer stay usable; links
// to product, cart or checkout pages are disabled and labelled.
const PURCHASE_PATH = /\/(cart|checkout|products)(\/|\?|$)/i
function applyDraftLock() {
  const draft = !!document.querySelector('[data-lp-draft-offer]')
  document.querySelectorAll('[data-lp-cta="link"], .lp a[href]').forEach((a) => {
    let path = ''
    try { path = new URL(a.getAttribute('href') || '', location.href).pathname } catch { return }
    const lock = draft && PURCHASE_PATH.test(path)
    a.toggleAttribute('data-lp-draft-disabled', lock)
    if (lock) { a.setAttribute('aria-disabled', 'true'); a.setAttribute('tabindex', '-1'); a.title = 'Draft: purchase is disabled until the product exists' }
    else if (a.hasAttribute('data-lp-was-locked')) { a.removeAttribute('aria-disabled'); a.removeAttribute('tabindex'); a.title = '' }
    if (lock) a.setAttribute('data-lp-was-locked', '')
  })
  document.querySelectorAll('[data-lp-draft-offer] button:not([data-lp-draft-safe]), [data-lp-draft-offer] input:not([data-lp-draft-safe]), [data-lp-draft-offer] select').forEach((el) => { el.disabled = true })
}

function refreshCtas() {
  applyDraftLock()
  // Soft links (the star cue): if the named section is missing, drop the link and keep the text.
  document.querySelectorAll('[data-lp-soft-target]').forEach((a) => {
    if (findAnchor(a.dataset.lpSoftTarget)) a.setAttribute('href', `#${a.dataset.lpSoftTarget}`)
    else a.removeAttribute('href')
  })
  // A scroll button whose target was removed or renamed is hidden rather than
  // left dead. In the editor it stays visible with a warning outline.
  document.querySelectorAll('[data-lp-cta="scroll"]').forEach((btn) => {
    const ok = !!findAnchor(btn.dataset.lpTarget)
    btn.toggleAttribute('data-lp-missing-target', !ok)
    if (window.Shopify && Shopify.designMode) {
      btn.style.outline = ok ? '' : '3px dashed #bf5d63'
      btn.title = ok ? '' : `No section named "${btn.dataset.lpTarget}" on this page`
      btn.hidden = false
    } else {
      btn.hidden = !ok
    }
  })
}

function carryCampaignParams(link) {
  let url
  try { url = new URL(link.href, location.href) } catch { return }
  if (url.origin !== location.origin) return
  const here = new URLSearchParams(location.search)
  here.forEach((value, key) => {
    if (CAMPAIGN_PARAM.test(key) && !url.searchParams.has(key)) url.searchParams.set(key, value)
  })
  link.href = url.toString()
}

document.addEventListener('click', (event) => {
  const locked = event.target.closest('[data-lp-draft-disabled]')
  if (locked) { event.preventDefault(); event.stopPropagation(); return }
  const btn = event.target.closest('[data-lp-cta]')
  if (!btn) return
  if (btn.dataset.lpCta === 'link') {
    carryCampaignParams(btn)
    return
  }
  event.preventDefault()
  const target = findAnchor(btn.dataset.lpTarget)
  if (!target) { refreshCtas(); return }
  scrollToSection(target)
})

function scrollToSection(target) {
  const done = () => {
    const focusable = target.querySelector('.lp-tier input:checked:not([disabled])') ||
      target.querySelector('input:not([disabled]), button:not([disabled]), a[href]')
    if (focusable) focusable.focus({ preventScroll: true })
  }
  if (reduceMotion()) {
    target.scrollIntoView({ behavior: 'auto', block: 'start' })
    return done()
  }
  const startY = window.scrollY
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  // Some themes/browsers cancel smooth scrolling (observed on this theme in
  // testing). If the page hasn't moved, jump instead.
  setTimeout(() => {
    if (Math.abs(window.scrollY - startY) < 2 && Math.abs(target.getBoundingClientRect().top) > 120) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' })
    }
    done()
  }, 450)
}

/* ---------- Flavor options: the rest of the page follows the buy box ---------- */

// v1.4.4: other sections can carry elements tagged data-lp-flavor="<key>" (shown only while
// that flavor is selected) and data-lp-flavor-token elements whose data-template has [flavor]
// (replaced by the flavor's name in headings and bars). The offer calls this on load and on
// every change; a page without flavor options never calls it.
function syncPageFlavor(offerRoot, key, title) {
  document.querySelectorAll('[data-lp-flavor]').forEach((el) => {
    if (offerRoot.contains(el)) return
    el.hidden = el.dataset.lpFlavor !== key
  })
  document.querySelectorAll('[data-lp-flavor-token]').forEach((el) => {
    const t = el.dataset.template
    if (t != null) el.textContent = t.replace(/\[flavor\]/g, title)
  })
  // Not data-lp-flavor: that attribute means 'show only for this flavor' and would hide the page.
  document.documentElement.dataset.lpActiveFlavor = key
}

/* ---------- Offer gallery ---------- */

function setupGallery(root, signal) {
  const gallery = root.querySelector('[data-lp-gallery]')
  if (!gallery) return null
  const track = gallery.querySelector('[data-lp-gallery-track]')
  const allSlides = [...gallery.querySelectorAll('[data-lp-slide]')]
  const allThumbs = [...gallery.querySelectorAll('[data-lp-thumb]')]
  if (!track || allSlides.length === 0) return null
  // v1.4.4: slides of an unselected flavor are hidden; only visible ones take part.
  let slides = allSlides.filter((s) => !s.hidden)
  let thumbs = allThumbs.filter((t) => !t.hidden)

  const go = (i, smooth = true) => {
    const slide = slides[i]
    if (!slide) return
    track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' })
  }
  let frame = 0
  const sync = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'))
    })
  }
  const refresh = () => {
    slides = allSlides.filter((s) => !s.hidden)
    thumbs = allThumbs.filter((t) => !t.hidden)
    thumbs.forEach((t, j) => t.setAttribute('aria-label', `Image ${j + 1} of ${thumbs.length}`))
    track.scrollTo({ left: 0, behavior: 'auto' })
    sync()
  }
  allThumbs.forEach((t) => t.addEventListener('click', () => go(thumbs.indexOf(t)), { signal }))
  track.addEventListener('scroll', sync, { signal, passive: true })
  track.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))
    go(Math.min(slides.length - 1, Math.max(0, i + (e.key === 'ArrowRight' ? 1 : -1))))
  }, { signal })
  sync()
  return {
    refresh,
    select(blockId) {
      const i = slides.findIndex((s) => s.id === `LpSlide-${blockId}`)
      if (i >= 0) go(i, false)
    },
  }
}

// Showcase drafts: the tiers can be picked so the page can be reviewed as it will
// behave, and the price row shows that tier's draft price. Nothing can be bought.
function setupDraftPreview(root, signal) {
  if (!root.hasAttribute('data-lp-draft-offer')) return null
  const inputs = [...root.querySelectorAll('input[data-lp-draft-preview]')]
  if (inputs.length === 0) return null
  const prices = [...root.querySelectorAll('[data-lp-main-price]')]
  const render = () => {
    const on = inputs.find((i) => i.checked) || inputs[0]
    prices.forEach((el) => { el.textContent = (on && on.dataset.price) || el.dataset.empty || '' })
  }
  inputs.forEach((i) => i.addEventListener('change', render, { signal }))
  render()
  return {
    select(blockId) {
      const input = inputs.find((i) => i.value === blockId)
      if (input) { input.checked = true; render() }
    },
  }
}

/* ---------- Offer / buy box ---------- */

function setupOffer(root, signal) {
  const gallery = setupGallery(root, signal)
  const draft = setupDraftPreview(root, signal)
  const form = root.querySelector('[data-lp-offer-form]')
  const dataEl = root.querySelector('[data-lp-offer-data]')
  if (!form || !dataEl) {
    return { selectBlock(blockId) { gallery?.select(blockId); draft?.select(blockId) } }
  }
  let data
  try { data = JSON.parse(dataEl.textContent) } catch (e) { console.error('[lp-kit] offer data unreadable', e); return }

  let tiersByVariant = new Map(data.tiers.map((t) => [String(t.variant), t]))
  // v1.4.4: flavor options (each with its own product and tier variants).
  const flavors = new Map((data.flavors || []).map((f) => [f.key, f]))
  const flavorInputs = [...form.querySelectorAll('[data-lp-flavor-input]')]
  const flavorEls = [...root.querySelectorAll('[data-lp-flavor]')]
  const flavorLive = form.querySelector('[data-lp-flavor-live]')
  let flavorKey = data.defaultFlavor || ''
  const addBtn = form.querySelector('[data-lp-add]')
  const status = form.querySelector('[data-lp-status]')
  const summary = form.querySelector('[data-lp-summary]')
  const planSelect = form.querySelector('[data-lp-plan]')
  const planWrap = form.querySelector('[data-lp-plan-wrap]')
  const modeInputs = [...form.querySelectorAll('[data-lp-mode]')]
  const mainPrices = [...root.querySelectorAll('[data-lp-main-price]')]
  const firstLabels = [...root.querySelectorAll('[data-lp-first-label]')]
  const modePrice = form.querySelector('[data-lp-mode-price]')
  const termsEl = form.querySelector('[data-lp-sub-terms]')
  const giftEl = form.querySelector('[data-lp-gift]')
  // v1.4 (Promo): the button's own label and price, the tier contents lists and the perks.
  const btnLabel = addBtn.querySelector('[data-lp-btn-label]')
  const btnNow = addBtn.querySelector('[data-lp-btn-now]')
  const btnAnchor = addBtn.querySelector('[data-lp-btn-anchor]')
  const contents = [...root.querySelectorAll('[data-lp-contents]')]
  const giftChips = [...root.querySelectorAll('[data-lp-gift-chip]')]
  const perks = [...form.querySelectorAll('[data-lp-perk][data-template]')]
  const setBtnLabel = (text) => { if (btnLabel) btnLabel.textContent = text; else addBtn.textContent = text }
  // v1.4: the phones' add bar (inside this form, so its button submits the same add).
  const bar = form.querySelector('[data-lp-offer-bar]')
  const barBtn = bar && bar.querySelector('[data-lp-bar-add]')
  const barLabel = bar && bar.querySelector('[data-lp-bar-label]')
  const barNow = bar && bar.querySelector('[data-lp-bar-now]')
  const barAnchor = bar && bar.querySelector('[data-lp-bar-anchor]')
  let busy = false

  const mode = () => (modeInputs.find((i) => i.checked)?.value || 'onetime')
  const selectedTier = () => {
    const input = form.querySelector('input[name="id"]:checked')
    return input ? tiersByVariant.get(input.value) : null
  }
  const priceFor = (tier) => {
    if (mode() === 'subscribe' && planSelect) return tier.plans[planSelect.value] || null
    return tier.onetime
  }
  // v1.3: a first-box price (plan price less the tier's first-order extra) is what the
  // shopper pays on the first subscription order, so it's the price shown while subscribed.
  const shownFor = (p) => (mode() === 'subscribe' && p && p.first ? p.first : p)
  const planName = () => (planSelect ? (data.planNames || {})[String(planSelect.value)] || '' : '')
  const giftUnlocked = () => !!(data.gift && (data.gift.unlock === 'always' || (mode() === 'subscribe' && planSelect)))

  function render() {
    // Each tier card shows the price for the current purchase type.
    form.querySelectorAll('.lp-tier').forEach((label) => {
      const input = label.querySelector('input[name="id"]')
      const tier = tiersByVariant.get(input.value)
      if (!tier) return
      const p = priceFor(tier)
      const shown = shownFor(p)
      const priceEl = label.querySelector('[data-lp-price]')
      const perEl = label.querySelector('[data-lp-per]')
      const anchorEl = label.querySelector('[data-lp-anchor-price]')
      const noPlan = mode() === 'subscribe' && !p
      const needsPlan = mode() === 'onetime' && tier.requiresPlan
      input.disabled = !tier.available || noPlan || needsPlan
      priceEl.textContent = shown ? shown.price : (noPlan ? 'One-time only' : data.strings.needsPlan)
      if (perEl) {
        perEl.textContent = shown && shown.perBag ? `${shown.perBag} ${data.strings.perBag}` : ''
        perEl.hidden = !(data.showPerBag && shown && shown.perBag)
      }
      if (anchorEl) {
        anchorEl.textContent = p && p.anchor && data.showAnchor ? p.anchor : ''
        anchorEl.hidden = !(p && p.anchor && data.showAnchor && p.anchor !== p.price)
      }
      // v1.4: the saving (one-time less the price shown) and the first-box caption.
      const saveEl = label.querySelector('[data-lp-save]')
      if (saveEl) {
        const save = mode() === 'subscribe' && shown && shown.save ? shown.save : ''
        saveEl.textContent = save ? `${data.strings.saveShort || 'Save'} ${save}` : ''
        saveEl.hidden = !(data.showSavings && save)
      }
      const firstEl = label.querySelector('[data-lp-tier-first]')
      if (firstEl) firstEl.hidden = !(p && shown && shown === p.first)
    })

    // If the chosen tier just became unavailable, move to the first one that isn't.
    let tier = selectedTier()
    const chosen = form.querySelector('input[name="id"]:checked')
    if (!chosen || chosen.disabled) {
      const next = form.querySelector('input[name="id"]:not([disabled])')
      if (next) next.checked = true
      tier = selectedTier()
    }

    if (planWrap) planWrap.hidden = mode() !== 'subscribe'
    if (planSelect) planSelect.disabled = mode() !== 'subscribe'

    const p = tier && priceFor(tier)
    const shown = shownFor(p)
    const canBuy = !!(tier && p && tier.available && (mode() === 'subscribe' || !tier.requiresPlan))
    if (!busy) addBtn.disabled = !canBuy
    const chosenInput = form.querySelector('input[name="id"]:checked')
    const fillerPrice = chosenInput && chosenInput.hasAttribute('data-lp-filler') ? chosenInput.dataset.price : ''
    mainPrices.forEach((el) => { el.textContent = shown ? shown.price : (fillerPrice || el.dataset.empty || '') })
    firstLabels.forEach((el) => { el.hidden = !(shown && p && shown === p.first) })
    if (modePrice && tier) modePrice.textContent = tier.onetime.price
    if (termsEl && tier && planSelect) {
      const sp = tier.plans[planSelect.value]
      termsEl.textContent = !sp ? '' : (sp.first ? data.strings.termsFirst : data.strings.terms)
        .replace('[first]', sp.first ? sp.first.price : '')
        .replace('[price]', sp.price)
        .replace('[plan]', planName())
    }
    if (giftEl) giftEl.dataset.state = giftUnlocked() ? 'unlocked' : 'locked'
    // v1.4: the chosen tier's contents list, the gift in it, the button price and perks.
    contents.forEach((el) => { el.hidden = !(tier && el.dataset.lpContents === tier.block) })
    giftChips.forEach((el) => { el.hidden = !giftUnlocked() })
    if (btnNow && !busy) {
      btnNow.textContent = shown ? shown.price : ''
      if (btnAnchor) {
        const anchor = p && p.anchor && data.showAnchor && p.anchor !== p.price ? p.anchor : ''
        btnAnchor.textContent = anchor
        btnAnchor.hidden = !anchor
      }
    }
    if (bar) {
      if (barBtn) barBtn.disabled = addBtn.disabled
      if (barNow) barNow.textContent = shown ? shown.price : ''
      if (barAnchor) {
        const anchor = p && p.anchor && data.showAnchor && p.anchor !== p.price ? p.anchor : ''
        barAnchor.textContent = anchor
        barAnchor.hidden = !anchor
      }
      const tierLabel = tier && form.querySelector(`[data-lp-tier="${CSS.escape(tier.block)}"] .lp-tier__label`)
      if (barLabel && tierLabel) barLabel.textContent = tierLabel.textContent
    }
    if (perks.length && tier && planSelect) {
      const sp = tier.plans[planSelect.value]
      perks.forEach((el) => {
        el.textContent = el.dataset.template
          .replace('[first]', sp ? (sp.first ? sp.first.price : sp.price) : '')
          .replace('[price]', sp ? sp.price : '')
          .replace('[plan]', planName())
          .replace('[onetime]', tier.onetime.price)
          .replace('[save]', sp && sp.save ? sp.save : '')
      })
    }
    if (summary) {
      if (!shown) summary.textContent = ''
      else {
        const parts = [`${data.strings.total} ${shown.price}`]
        if (shown.perBag && data.showPerBag) parts.push(`${shown.perBag} ${data.strings.perBag}`)
        if (p.save && shown === p) parts.push(`${data.strings.save} ${p.save}`)
        if (p.free && data.strings.freeShipping) parts.push(data.strings.freeShipping)
        summary.textContent = parts.join(' · ')
      }
    }
  }

  // v1.3: keep this offer's extras in step with what's actually in the cart. The site's
  // cart drawer lets shoppers change lines after adding, so the gift and any tier code
  // follow the cart, not the last click: a tier's code stays while a subscribed line of
  // that tier is in the cart, the gift never exceeds its quantity, and it goes once no
  // qualifying line is left. Codes the shopper entered themselves are left alone.
  const everyTier = [...data.tiers, ...[...flavors.values()].flatMap((f) => f.tiers || [])]
  const tierCodes = new Map(everyTier.filter((t) => t.discountCode).map((t) => [String(t.variant), t.discountCode]))
  const tierVariants = new Set(everyTier.map((t) => String(t.variant)))
  // v1.4: a message-only gift (addToCart false) is shown on the page but never added to the cart.
  const giftLine = !!(data.gift && data.gift.addToCart !== false)
  const hasExtras = tierCodes.size > 0 || giftLine
  const isOurGift = (line) => !!data.gift && String(line.variant_id) === String(data.gift.variant) &&
    !!line.properties && line.properties._free_gift === 'true' && (!data.lineKey || line.properties._gift_set === data.lineKey)
  const qualifies = (line) => tierVariants.has(String(line.variant_id)) &&
    (data.gift && data.gift.unlock === 'always' ? true : !!line.selling_plan_allocation)
  const postCart = async (siteCart, body) => {
    if (siteCart && typeof siteCart.update === 'function') return siteCart.update(body)
    return (await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })).json()
  }
  let syncing = false
  let lastCodes = null
  async function syncCart(cartData, siteCart) {
    if (!hasExtras || syncing) return
    syncing = true
    try {
      let cart = cartData && Array.isArray(cartData.items) ? cartData : await (await fetch('/cart.js')).json()
      if (giftLine) {
        let left = (cart.items || []).some(qualifies) ? (Number(data.gift.quantity) || 1) : 0
        const updates = {}
        ;(cart.items || []).filter(isOurGift).forEach((g) => {
          const q = Math.min(g.quantity, left)
          left -= q
          if (q !== g.quantity) updates[g.key] = q
        })
        if (Object.keys(updates).length) cart = (await postCart(siteCart, { updates })) || cart
      }
      if (tierCodes.size) {
        const wanted = new Set()
        ;(cart.items || []).forEach((l) => { const c = tierCodes.get(String(l.variant_id)); if (c && l.selling_plan_allocation) wanted.add(c) })
        const ours = new Set([...tierCodes.values()].map((c) => c.toLowerCase()))
        // A cart without discount_codes can't say what's applied, so remember what this
        // offer last sent and never send the same list twice (no update loop either way).
        const known = Array.isArray(cart.discount_codes)
        const current = known ? cart.discount_codes.map((d) => d.code) : []
        const next = [...current.filter((c) => !ours.has(String(c).toLowerCase())), ...wanted].join(',')
        const unchanged = known ? next.toLowerCase() === current.join(',').toLowerCase() : next === (lastCodes ?? '')
        if (!unchanged && next !== lastCodes) {
          lastCodes = next
          await postCart(siteCart, { discount: next })
        }
      }
    } catch (e) {
      console.warn('[lp-kit] cart sync skipped', e)
    } finally {
      syncing = false
    }
  }
  if (hasExtras) document.addEventListener('cart:update', (e) => { if (!busy) syncCart(e.detail, window.cart) }, { signal })

  function setStatus(message, isError) {
    status.textContent = message || ''
    status.classList.toggle('lp-offer__status--error', !!isError)
  }

  function readError(err) {
    // The error carries Shopify's JSON reply ({ status, message, description }).
    try {
      const body = typeof err === 'string' ? JSON.parse(err) : JSON.parse(err.message)
      return body.description || body.message || data.strings.error
    } catch { return data.strings.error }
  }

  async function add(event) {
    event.preventDefault()
    if (busy) return
    const tier = selectedTier()
    const p = tier && priceFor(tier)
    if (!tier || !p || addBtn.disabled) return

    // Lock first: every await below would otherwise let a second tap start a second add.
    busy = true
    addBtn.disabled = true
    if (barBtn) barBtn.disabled = true
    addBtn.setAttribute('aria-busy', 'true')
    setBtnLabel(addBtn.dataset.busyLabel || addBtn.dataset.label)
    setStatus('')
    const subscribed = mode() === 'subscribe' && !!planSelect
    const withGift = giftLine && giftUnlocked()
    const item = { id: Number(tier.variant), quantity: 1 }
    if (subscribed) item.selling_plan = Number(planSelect.value)
    // Mirror the theme's own buy box: campaign parameters travel as hidden line properties.
    const utm = {}
    new URLSearchParams(location.search).forEach((v, k) => { if (CAMPAIGN_PARAM.test(k)) utm[`_${k}`] = v.slice(0, 500) })
    const props = { ...utm }
    // v1.3: with a cart line key, the same hidden properties the theme buy box writes, so
    // the store's discounts (set up against those properties) treat these lines the same.
    const key = data.lineKey
    if (key) props._data = `f:${key},t:m,s:${subscribed ? 1 : 0}${tier.bags ? `,v:${tier.bags}` : ''}`
    if (Object.keys(props).length) item.properties = props
    const items = [item]
    // One gift per cart: if this offer's gift is already there, don't add a second.
    let giftInCart = false
    if (withGift) {
      try { giftInCart = ((await (await fetch('/cart.js')).json()).items || []).some(isOurGift) } catch { giftInCart = false }
    }
    if (withGift && !giftInCart) {
      const giftProps = { ...utm, _gift: 'true', _free_gift: 'true' }
      if (key) { giftProps._data = `f:${key},t:bg,g:1`; giftProps._gift_set = key }
      items.push({ id: Number(data.gift.variant), quantity: Number(data.gift.quantity) || 1, properties: giftProps })
    }

    try {
      const siteCart = window.cart
      if (siteCart && typeof siteCart.add === 'function' && typeof siteCart.open === 'function') {
        // The site's own cart (assets/cart.tsx), the same call the product page's buy
        // button makes: add, then refresh and slide out the site's cart drawer.
        // Never the former agency's slide cart or window.addToCart, and never a redirect.
        const reply = await siteCart.add({ items }, false)
        if (!reply || !Array.isArray(reply.items)) throw new Error(JSON.stringify(reply || {}))
        await syncCart(null, siteCart)
        if (data.cartDrawer === 'zipify' && window.Zipify && window.Zipify.Cart) {
          // Opt-in (lp-offer "Cart after adding"): refresh the theme's cart state quietly,
          // then open the site's Zipify (OneClickUpsell) cart, the product pages' cart.
          // ocu:cart:open:updated is the event Zipify's own integrations dispatch.
          if (typeof siteCart.get === 'function') await siteCart.get()
          window.dispatchEvent(new CustomEvent('ocu:cart:open:updated'))
        } else {
          await siteCart.open()
        }
      } else {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        })
        if (!res.ok) throw new Error(await res.text())
        await syncCart(null, null)
        // The site's cart drawer listens for these (assets/cart.tsx, slide-in-drawers.tsx).
        const cartData = await (await fetch('/cart.js')).json()
        document.dispatchEvent(new CustomEvent('cart:update', { detail: cartData }))
        document.dispatchEvent(new CustomEvent('cart:open'))
      }
      setStatus(data.strings.added)
      root.dispatchEvent(new CustomEvent('lp:cart:added', { bubbles: true, detail: { item, items, sectionId: data.sectionId } }))
    } catch (err) {
      setStatus(readError(err), true)
      root.dispatchEvent(new CustomEvent('lp:cart:error', { bubbles: true, detail: { item, items, sectionId: data.sectionId } }))
    } finally {
      busy = false
      addBtn.removeAttribute('aria-busy')
      setBtnLabel(addBtn.dataset.label)
      render()
    }
  }

  // v1.4.4: show one flavor. Tagged elements of the others hide, every pack tile takes the
  // flavor's variant (a pack with no variant on that product hides), the gallery restarts on
  // the flavor's first image, and the chosen pack stays chosen where it exists.
  function applyFlavor(key) {
    const f = flavors.get(key)
    // A flavor the page doesn't know, or one with no variants of its own, is never shown:
    // the tiles would otherwise sell another flavor's variants under its name.
    if (!f || !Array.isArray(f.tiers) || f.tiers.length === 0) return
    flavorKey = key
    const byBlock = new Map((f.tiers || []).map((t) => [t.block, t]))
    form.querySelectorAll('.lp-tier').forEach((label) => {
      const input = label.querySelector('input[name="id"]')
      const t = byBlock.get(label.dataset.lpTier)
      if (t) { input.value = String(t.variant); label.hidden = false }
      else { label.hidden = true; input.checked = false; input.disabled = true }
    })
    tiersByVariant = new Map((f.tiers || []).map((t) => [String(t.variant), t]))
    flavorEls.forEach((el) => { el.hidden = el.dataset.lpFlavor !== key })
    flavorInputs.forEach((i) => { i.checked = i.value === key })
    root.dataset.lpFlavor = key
    gallery?.refresh?.()
    render()
    syncPageFlavor(root, key, f.title || f.name)
    if (flavorLive) flavorLive.textContent = `${f.name} selected`
    root.dispatchEvent(new CustomEvent('lp:flavor:change', { bubbles: true, detail: { flavor: key, productHandle: f.productHandle, sectionId: data.sectionId } }))
  }
  flavorInputs.forEach((i) => i.addEventListener('change', () => { if (i.checked) applyFlavor(i.value) }, { signal }))
  if (flavors.size) {
    root.dataset.lpFlavor = flavorKey
    const want = (new URLSearchParams(location.search).get('flavor') || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    // ?variant=<id> (the product page's own deep link) picks the flavor and the pack that own it.
    const wantVariant = (new URLSearchParams(location.search).get('variant') || '').trim()
    const byVariant = wantVariant && [...flavors.values()].find((f) => (f.tiers || []).some((t) => String(t.variant) === wantVariant))
    // A browser that restores form state on reload can leave another tile checked: follow it.
    const restored = flavorInputs.find((i) => i.checked)?.value
    const start = byVariant ? byVariant.key : (want && flavors.has(want) ? want : restored)
    if (start && start !== flavorKey && flavors.has(start)) applyFlavor(start)
    else {
      flavorInputs.forEach((i) => { i.checked = i.value === flavorKey })
      const f0 = flavors.get(flavorKey)
      if (f0) syncPageFlavor(root, flavorKey, f0.title || f0.name)
    }
    if (byVariant) {
      const input = form.querySelector(`input[name="id"][value="${CSS.escape(wantVariant)}"]`)
      if (input && !input.disabled) { input.checked = true; render() }
    }
  }

  form.addEventListener('change', render, { signal })
  form.addEventListener('submit', add, { signal })
  render()

  // v1.4: show the phones' add bar once the shopper has reached the choice and while the
  // add button itself is off screen; hide it again whenever the offer is out of view.
  if (bar) {
    const tiers = form.querySelector('.lp-offer__fieldset')
    let frame = 0
    const place = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const vh = window.innerHeight
        const r = root.getBoundingClientRect()
        const b = addBtn.getBoundingClientRect()
        const t = tiers ? tiers.getBoundingClientRect() : r
        const show = window.matchMedia('(max-width: 749px)').matches && r.top < vh && r.bottom > 0 &&
          t.top < vh * 0.45 && (b.top > vh || b.bottom < 0)
        bar.classList.toggle('is-visible', show)
        bar.setAttribute('aria-hidden', show ? 'false' : 'true')
        bar.inert = !show
      })
    }
    window.addEventListener('scroll', place, { signal, passive: true })
    window.addEventListener('resize', place, { signal, passive: true })
    place()
  }

  return {
    selectBlock(blockId) {
      // v1.4.4: a block that belongs to another flavor (or is a flavor's tile) shows that flavor first.
      const tile = root.querySelector(`[data-lp-flavor-tile][data-lp-block="${CSS.escape(blockId)}"]`)
      const owner = tile ? tile.dataset.lpFlavorTile : root.querySelector(`#LpSlide-${CSS.escape(blockId)}[data-lp-flavor], [data-lp-flavor][data-lp-block="${CSS.escape(blockId)}"]`)?.dataset.lpFlavor
      if (owner && owner !== flavorKey) applyFlavor(owner)
      gallery?.select(blockId)
      const input = root.querySelector(`[data-lp-tier="${CSS.escape(blockId)}"] input`)
      if (input && !input.disabled) { input.checked = true; render() }
    },
  }
}

/* ---------- Reviews carousel ---------- */

function setupReviews(root, signal) {
  const track = root.querySelector('[data-lp-track]')
  const nav = root.querySelector('[data-lp-nav]')
  if (!track || !nav) return {
    selectBlock(id) { root.querySelector(`[data-lp-block="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'nearest' }) },
  }
  const step = () => track.clientWidth * 0.85
  const update = () => { nav.hidden = track.scrollWidth <= track.clientWidth + 4 }
  nav.querySelector('[data-lp-prev]').addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion() ? 'auto' : 'smooth' }), { signal })
  nav.querySelector('[data-lp-next]').addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion() ? 'auto' : 'smooth' }), { signal })
  const ro = new ResizeObserver(update)
  ro.observe(track)
  signal.addEventListener('abort', () => ro.disconnect())
  update()
  return {
    selectBlock(id) {
      const card = track.querySelector(`[data-lp-block="${CSS.escape(id)}"]`)
      if (card) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'auto' })
    },
  }
}

/* ---------- FAQ ---------- */

function setupFaq(root) {
  return {
    selectBlock(id) {
      const item = root.querySelector(`[data-lp-block="${CSS.escape(id)}"]`)
      if (item) item.open = true
    },
  }
}

/* ---------- Sticky bar ---------- */

function setupSticky(root, signal) {
  const bar = root.querySelector('[data-lp-sticky]')
  if (!bar) return
  if (window.Shopify && Shopify.designMode) return // the editor shows a note instead
  const hideNearName = root.dataset.lpHideNear
  let pastHero = false
  let nearOffer = false
  let observer = null

  const apply = () => {
    const show = pastHero && !nearOffer
    bar.classList.toggle('is-visible', show)
    bar.setAttribute('aria-hidden', show ? 'false' : 'true')
    bar.inert = !show
  }

  const watch = () => {
    observer?.disconnect()
    pastHero = false
    nearOffer = false
    const sentinel = document.querySelector('[data-lp-sentinel]')
    const hideNear = findAnchor(hideNearName)
    observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === sentinel) pastHero = !e.isIntersecting && e.boundingClientRect.top < 0
        if (e.target === hideNear) nearOffer = e.isIntersecting
      })
      apply()
    })
    if (sentinel) observer.observe(sentinel)
    else pastHero = true // no hero on this page: show once the page loads
    if (hideNear) observer.observe(hideNear)
    apply()
  }

  watch()
  signal.addEventListener('abort', () => observer?.disconnect())
  return { rewatch: watch }
}

/* ---------- Lifecycle ---------- */

const SETUP = { offer: setupOffer, reviews: setupReviews, faq: setupFaq, sticky: setupSticky }

function initSection(el) {
  if (instances.has(el)) return
  const type = el.dataset.lpSection
  const controller = new AbortController()
  const api = SETUP[type] ? SETUP[type](el, controller.signal) || {} : {}
  instances.set(el, { controller, api })
}

function destroySection(el) {
  const inst = instances.get(el)
  if (!inst) return
  inst.controller.abort()
  instances.delete(el)
}

function initWithin(scope) {
  scope.querySelectorAll('[data-lp-section]').forEach(initSection)
  refreshCtas()
}

function eachInstance(fn) {
  document.querySelectorAll('[data-lp-section]').forEach((el) => {
    const inst = instances.get(el)
    if (inst) fn(el, inst)
  })
}

initWithin(document)

// Theme editor events (Shopify.designMode). Harmless on the storefront.
document.addEventListener('shopify:section:load', (e) => {
  initWithin(e.target)
  eachInstance((el, inst) => inst.api.rewatch?.())
})
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-lp-section]').forEach(destroySection)
  // The section is still in the DOM during unload; re-check once it is gone.
  setTimeout(() => { refreshCtas(); eachInstance((el, inst) => inst.api.rewatch?.()) }, 0)
})
document.addEventListener('shopify:section:reorder', () => {
  refreshCtas()
  eachInstance((el, inst) => inst.api.rewatch?.())
})
document.addEventListener('shopify:block:select', (e) => {
  const sectionEl = e.target.closest('[data-lp-section]')
  const inst = sectionEl && instances.get(sectionEl)
  inst?.api.selectBlock?.(e.detail.blockId)
})

// Exposed for QA scripts and debugging only.
window.LPKit = { version: '1.4', initWithin, destroySection, refreshCtas, applyDraftLock }
